// Purpose: Best-effort reCAPTCHA Enterprise tokens for public applicant API calls.
// Never throws: if the script is unavailable or slow, the request proceeds without a token and
// the backend decides which side effects to allow.
import { logger } from '@/features/help/utils/logger';

const SCRIPT_WAIT_MS = 4000;
const EXECUTE_TIMEOUT_MS = 6000;
const POLL_INTERVAL_MS = 100;

const waitForRecaptcha = async (): Promise<any | null> => {
  const deadline = Date.now() + SCRIPT_WAIT_MS;
  while (Date.now() < deadline) {
    const enterprise = (window.grecaptcha as any)?.enterprise;
    if (enterprise?.execute) {
      return enterprise;
    }
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
  }
  return null;
};

export const getRecaptchaToken = async (action: string): Promise<string | null> => {
  const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
  if (!siteKey || typeof window === 'undefined') {
    return null;
  }

  try {
    const enterprise = await waitForRecaptcha();
    if (!enterprise) {
      logger.warn('Recaptcha', 'reCAPTCHA unavailable; continuing without token', { action });
      return null;
    }

    const token = await Promise.race<string | null>([
      new Promise<string | null>((resolve) => {
        enterprise.ready(async () => {
          try {
            resolve(await enterprise.execute(siteKey, { action }));
          } catch (error) {
            logger.warn('Recaptcha', 'reCAPTCHA execution failed; continuing without token', {
              action,
              error,
            });
            resolve(null);
          }
        });
      }),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), EXECUTE_TIMEOUT_MS)),
    ]);
    return token || null;
  } catch (error) {
    logger.warn('Recaptcha', 'reCAPTCHA token request failed; continuing without token', {
      action,
      error,
    });
    return null;
  }
};

export const getRecaptchaHeaders = async (action: string): Promise<Record<string, string>> => {
  const token = await getRecaptchaToken(action);
  return token ? { 'x-recaptcha-token': token } : {};
};
